/* import __COLOCATED_TEMPLATE__ from './reasons-interactive-bar.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { inject as service } from '@ember/service';

export interface ReasonData {
  share: number;
  sentToTeammate: number;
  resolved: number;
  unresolved: number;
}

type ReasonKey = 'contentGaps' | 'dataGaps' | 'actionGaps';

interface Signature {
  contentGaps: ReasonData;
  dataGaps: ReasonData;
  actionGaps: ReasonData;
  filterBy: (suggestionType: ReasonKey) => void;
}

type Segment = {
  key: ReasonKey;
  label: string;
  colorClass: string;
  share: number;
};

export default class ReasonsInteractiveBar extends Component<Signature> {
  @tracked selected: ReasonKey | null = null;
  @service declare intl: IntlService;

  get segments(): Segment[] {
    return [
      {
        key: 'contentGaps',
        label: 'CONTENT GAPS',
        colorClass: 'bg-vis-mantis-10',
        share: Math.round(this.args.contentGaps.share),
      },
      {
        key: 'dataGaps',
        label: 'DATA GAPS',
        colorClass: 'bg-vis-mantis-40',
        share: Math.round(this.args.dataGaps.share),
      },
      {
        key: 'actionGaps',
        label: 'ACTION GAPS',
        colorClass: 'bg-vis-mantis-60',
        share: Math.round(this.args.actionGaps.share),
      },
    ];
  }

  get gridTemplateColumns() {
    let spans = this.segments.map((segment) => (segment.share > 0 ? segment.share : 1));
    return spans.map((span) => `${span}fr`).join(' ');
  }

  get hasSelection() {
    return this.selected !== null;
  }

  @action
  isDimmed(key: ReasonKey): boolean {
    return this.selected !== null && this.selected !== key;
  }

  @action
  handleSelect(key: ReasonKey) {
    if (this.selected === key) {
      // Toggle off: clear selection and notify parent to clear filter
      this.selected = null;
      this.args.filterBy?.(key);
      return;
    }

    this.selected = key;
    this.args.filterBy?.(key);
  }

  @action
  getReasonData(key: ReasonKey): ReasonData {
    return this.args[key];
  }

  @action
  getReasonTitle(key: ReasonKey): string {
    let titles: Record<ReasonKey, string> = {
      contentGaps: 'CUSTOMER CONTENT GAPS',
      dataGaps: 'CUSTOMER DATA GAPS',
      actionGaps: 'CUSTOMER ACTION GAPS',
    };
    return titles[key];
  }

  @action
  getReasonDescription(key: ReasonKey): string {
    let descriptions: Record<ReasonKey, string> = {
      contentGaps: this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.content-gaps',
      ),
      dataGaps: this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.data-gaps',
      ),
      actionGaps: this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.action-gaps',
      ),
    };
    return descriptions[key];
  }

  @action
  getReasonIcon(key: ReasonKey): InterfaceIconName {
    let icons: Record<ReasonKey, InterfaceIconName> = {
      contentGaps: 'knowledge',
      dataGaps: 'recipient-data',
      actionGaps: 'webhook',
    };
    return icons[key];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::ReasonsInteractiveBar': typeof ReasonsInteractiveBar;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/reasons-interactive-bar': typeof ReasonsInteractiveBar;
  }
}
